import React, { Suspense, Component } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import ScrollProgress from './components/animations/ScrollProgress';

/* Lazy-loaded pages for code splitting */
const Home = React.lazy(() => import('./pages/Home'));
const Platform = React.lazy(() => import('./pages/Platform'));
const Solutions = React.lazy(() => import('./pages/Solutions'));
const Company = React.lazy(() => import('./pages/Company'));
const Trademark = React.lazy(() => import('./pages/Trademark'));

/* Loading fallback */
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="w-8 h-8 border-3 border-brand-200 border-t-brand-500 rounded-full animate-spin" />
  </div>
);

/* Error Boundary */
interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-content-primary mb-4">Something went wrong</h1>
            <p className="text-content-secondary mb-6">Please refresh the page to try again.</p>
            <button onClick={() => window.location.reload()} className="btn-primary text-base">
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* 404 Page */
const NotFound = () => (
  <div className="min-h-screen bg-white text-content-primary flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-6xl font-bold mb-4">404</h1>
      <p className="text-xl text-content-secondary mb-8">Page not found</p>
      <Link
        to="/"
        className="text-brand-500 hover:text-brand-600 transition-colors text-lg font-medium"
      >
        Back to Home
      </Link>
    </div>
  </div>
);

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ScrollProgress />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/platform" element={<Platform />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/company" element={<Company />} />
          <Route path="/trademark" element={<Trademark />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default App;
